<template>
    <form-modal
        ref="formModal"
        :title="isPosCategoryCreated ? i18nEditPosCategory : i18nNewPosCategory"
        :in-editing="true"
        :handle-close="handleCancel">
        <template v-slot:form>
            <form @submit.prevent="handleOk">
                <b-form-row>
                    <b-col md="12">
                        <b-form-group
                            :label="i18nName"
                            label-for="name-input"
                            class="required label-pdv"
                            :invalid-feedback="i18nCategoryInvalidNameLabel"
                        >
                            <b-form-input
                                id="name-input"
                                v-model.trim="$v.posCategoryModal.name.$model"
                                class="input-pdv-blue"
                                name="name"
                                type="text"
                                :disabled="submitLoading"
                                :state="validateField('name', 'posCategoryModal')"
                                autofocus
                                autocomplete="off"
                                @input="clearResponseError('name', 'posCategoryModal')"
                                @blur="$v.posCategoryModal.name.$touch"
                            ></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-form-row>
            </form>
        </template>
        <template v-slot:footer>
            <save-button
                v-if="isPosCategoryCreated"
                ref="okButton"
                :busy="submitLoading"
                @onClick="handleOk"
            />
            <add-button
                v-else
                ref="okButton"
                :busy="submitLoading"
                @onClick="handleOk"
            />
            <cancel-label-button
                ref="cancelButton"
                :busy="submitLoading"
                @onClick="handleCancel"
            />
        </template>
    </form-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators'

import PosCategoriesService from '@src/services/PosCategoriesService'

import FormModal from '@/src/components/FormModal.vue'
import swalFeedback from '@src/mixins/swalFeedback.vue'
import validationMixin from '@src/mixins/validation.vue'
import fieldsValidation from '@src/mixins/fieldsValidation.vue'
import CancelLabelButton from '@/src/components/CancelLabelButton'
import AddButton from '@/src/components/AddButton'
import SaveButton from '@/src/components/SaveButton'
import cloneObject from '@utils/clone-object'

const i18nKey = 'POS'
const i18nCommon = 'COMMON'

export default {
    components: {
        FormModal,
        CancelLabelButton,
        AddButton,
        SaveButton,
    },
    mixins: [validationMixin, swalFeedback, fieldsValidation],
    data() {
        return {
            i18nKey,
            i18nCommon,
            posCategoryModal: {},
            submitLoading: false,
        }
    },
    validations: {
        posCategoryModal: {
            name: {
                required,
            },
        },
    },
    computed: {
        isPosCategoryCreated() {
            return this.posCategoryModal.id > 0
        },
        modalOperation() {
            return this.isPosCategoryCreated ? 'edited' : 'created'
        },
        i18nName() {
            return this.getI18n(i18nCommon, 'name')
        },
        i18nCategoryInvalidNameLabel() {
            return this.getI18n('ERROR_CODES.invalid_name')
        },
        i18nNewPosCategory() {
            return this.getI18n(i18nKey, 'BUTTONS.new_category')
        },
        i18nEditPosCategory() {
            return `${this.getI18n(i18nCommon, 'edit')} ${this.getI18n(
                i18nKey,
                'TITLES.category'
            )}`
        },
        i18nPosCategory() {
            return this.getI18nModified({
                prefix: i18nKey + '.TITLES.category',
                modifier: 1,
            })
        },
    },
    methods: {
        showModal(data) {
            this.submitLoading = false

            if (data) {
                this.posCategoryModal = cloneObject(data);
            } else {
                this.posCategoryModal = {
                    id: 0,
                    name: '',
                };
            }

            this.$refs.formModal.show()
            this.resetValidation()
        },
        clearAllResponseError() {
            this.modalResponseErrors = {}
        },
        handleCancel() {
            this.resetValidation()
            this.$refs.formModal.hide()
        },
        resetValidation() {
            this.$nextTick(() => this.$v.$reset())
            this.clearAllResponseError()
        },
        handleOk() {
            this.$v.posCategoryModal.$touch()
            if (!this.$v.posCategoryModal.$invalid && !this.submitLoading) {
                this.submitLoading = true
                this.handleSubmit()
            }
        },
        async handleSubmit() {
            const operation = this.isPosCategoryCreated
                ? PosCategoriesService.update(
                      this.posCategoryModal.id,
                      this.posCategoryModal
                  )
                : PosCategoriesService.create(this.posCategoryModal)

            const response = await operation
                .then((response) => response)
                .catch((error) => error)

            const statusCode = response.status.toString()

            if (statusCode.charAt(0) === '2') {
                this.$emit('refresh')
                this.resetValidation()
                this.$refs.formModal.hide()
                this.positiveFeedback(
                    this.i18nPosCategory,
                    this.modalOperation,
                    3
                )
            }

            if (statusCode === '422') {
                this.modalResponseErrors = response.data.errors
            }

            this.submitLoading = false
        },
    },
}
</script>
